import styled from 'styled-components';

export const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  background: ${({ theme }) => theme.color.gray[100]};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
`;

export const CalcGridCard = styled.section`
  width: 100%;
  max-width: 920px;
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-radius: 8px;
  padding: 20px 24px;
`;

export const CalcGridHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};

  h3 {
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.gray[500]};
  }

  p {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    color: ${({ theme }) => theme.color.gray[400]};
  }
`;

export const CalcSummary = styled.div`
  text-align: right;

  .label {
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.gray[400]};
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .value {
    margin-top: 2px;
    font-size: 20px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.blue[500]};
  }
`;

export const CalcTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 8px 10px;
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.gray[400]};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
  }

  th.num,
  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: 500;
  }

  td {
    padding: 10px;
    color: ${({ theme }) => theme.color.gray[750]};
    border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

export const RowDownloadButton = styled.button`
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

export const SourcePill = styled.span<{ kind: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ theme, kind }) => {
    switch (kind) {
      case 'BANK_LINKING':
        return theme.color.yellow[100];
      case 'CONNECTED_PAYROLL':
        return theme.color.blue[100];
      case 'TAX_RETURN_LINKING':
        return theme.color.purple[100];
      default:
        return theme.color.blue[75];
    }
  }};
  color: ${({ theme, kind }) => {
    switch (kind) {
      case 'BANK_LINKING':
        return theme.color.yellow[800];
      case 'CONNECTED_PAYROLL':
        return theme.color.blue[800];
      case 'TAX_RETURN_LINKING':
        return theme.color.purple[700];
      default:
        return theme.color.blue[700];
    }
  }};
`;

export const CalcEmpty = styled.div`
  text-align: center;
  padding: 24px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const DownloadRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 720px;
  margin-top: auto;
  margin-bottom: auto;
`;

export const DownloadCard = styled.button`
  flex: 1 1 320px;
  max-width: 340px;
  padding: 24px;
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.12s;

  &:hover {
    border-color: ${({ theme }) => theme.color.blue[500]};
    box-shadow: 0 6px 16px rgba(0, 159, 251, 0.12);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.gray[500]};
  }
`;

export const DownloadCardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.blue[75]};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 22px;
    height: 22px;
  }
`;

export const DownloadCardScope = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.gray[400]};

  strong {
    color: ${({ theme }) => theme.color.gray[750]};
    font-weight: 600;
  }
`;

export const DownloadCardFootnote = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.color.gray[150]};
  font-size: 11px;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const DownloadCardArrow = styled.span`
  color: ${({ theme }) => theme.color.blue[500]};
  font-size: 14px;
  font-weight: 600;
`;

const FIELD_CONTROL_HEIGHT = '40px';

export const BottomBar = styled.div`
  flex-shrink: 0;
  padding: 12px 24px;
  background: ${({ theme }) => theme.color.white};
  border-top: 1px solid ${({ theme }) => theme.color.gray[150]};
  display: flex;
  flex-direction: column;
  gap: 12px;

  .input-form {
    height: ${FIELD_CONTROL_HEIGHT};
    box-sizing: border-box;
    padding: 0 0.625rem;
    align-items: center;
  }
  .input-form input {
    height: 100%;
  }

  [role='dropdown'] .DDButton {
    height: ${FIELD_CONTROL_HEIGHT};
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }
  [role='dropdown'] .DDButton .DDSubContainer {
    padding: 0 0.625rem;
    width: 100%;
  }

  [role='dropdown'] > div:not(.DDButton) {
    bottom: calc(100% + 2px);
    top: auto !important;
    border-radius: 0.188rem 0.188rem 0 0;
    z-index: 10;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
  }
`;

export const BottomBarNote = styled.div`
  && .input-form {
    height: auto;
    padding: 0.5rem 0.625rem;
    align-items: stretch;
  }
`;

export const BottomBarRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

export const BottomBarCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .label-checkbox {
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.gray[500]};
  }
`;

export const BottomBarField = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;

  > div {
    margin-bottom: 0;
  }
`;

export const BottomBarAction = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-self: flex-end;
  margin-left: auto;

  button {
    height: ${FIELD_CONTROL_HEIGHT};
    padding: 0 20px;
  }
`;

export const BottomBarHelper = styled.p`
  margin: 0;
  align-self: center;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const FieldLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${({ theme }) => theme.color.gray[500]};
  margin-bottom: 6px;
`;
